import { type FormValues, getEmptyFormValues } from "@/widgets/classic-form/form";
import { populateFormValuesFromMetadata } from "@/widgets/classic-form/metadata-converters/populateFormValuesFromMetadata";
import type { FormMetadataResponse } from "@/entities/form-metadata";

interface GetResetBaseFormValuesArgs {
    isEditForm: boolean;
    formMetadata: FormMetadataResponse;
    defaultFormValues: FormValues;
    defaultYearProximity?: number | null;
}

/**
 * Computes the baseline the form is reset to when applying or clearing a
 * quick-fill selection.
 *
 * On the main search form, `defaultFormValues` is the correct baseline: it holds
 * the user-preference-driven initial state with no user-entered search data.
 *
 * On the edit form, `defaultFormValues` is derived from the previously-submitted
 * search (events, family, fields, treePerson), so reusing it would restore that
 * prior search instead of clearing it. Instead we build an empty-sourced baseline
 * from the metadata — structurally valid (non-removable groups keep a default
 * instance) but carrying no prior search values — so selecting or deselecting a
 * person starts from a clean slate.
 */
export const getResetBaseFormValues = ({
    isEditForm,
    formMetadata,
    defaultFormValues,
    defaultYearProximity = null,
}: GetResetBaseFormValuesArgs): FormValues =>
    isEditForm
        ? populateFormValuesFromMetadata({
              isEditForm,
              formMetadata,
              sourceFormValues: getEmptyFormValues(),
              matchAllTermsExactly: false,
              defaultYearProximity,
          })
        : defaultFormValues;
